import { motion } from 'motion/react';
import { cn } from '@/lib/utils';

interface OrdersDrilldownSlidingToggleProps<T extends string> {
  value: T;
  options: readonly T[];
  getLabel: (option: T) => string;
  /** Optional colored dot before the label. */
  getColor?: (option: T) => string | undefined;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** Unique per toggle on screen: the active pill glides between options. */
  layoutId: string;
}

export function OrdersDrilldownSlidingToggle<T extends string>({
  value,
  options,
  getLabel,
  getColor,
  onChange,
  ariaLabel,
  layoutId,
}: OrdersDrilldownSlidingToggleProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="relative inline-flex rounded-lg bg-muted p-0.5"
    >
      {options.map((option) => {
        const isActive = option === value;
        const color = getColor?.(option);
        return (
          <button
            key={option}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option)}
            className={cn(
              'relative rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {isActive ? (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 rounded-md bg-background shadow-sm"
                transition={{ type: 'spring', stiffness: 500, damping: 38 }}
              />
            ) : null}
            <span className="relative flex items-center gap-1.5">
              {color ? (
                <span
                  className="inline-block size-2 rounded-full"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
              ) : null}
              {getLabel(option)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
